// @givmo/app-admin — the Enrollment CSV (EP-WEB7).
//
// The ONE genuinely client-built export on this surface. The other three (transactions, tax, impact) are
// server-rendered endpoints; there is deliberately no server-side roster export, so this pages
// `/admin/company/employees` to exhaustion and writes the CSV here.
//
// **Column choice is a privacy decision, not a convenience.** The roster row once carried the
// employee's `birth_date`, `phone_number`, `recovery_email` and PERSONAL `email` beside their
// `work_email`; Company Profiles' CAM-R141 took them off the wire, and their CAM-R170 may bring the
// phone number, birthday and location back. Dumping the row would export an employee's date of birth
// and personal contact details to a spreadsheet on an admin's laptop, which nobody asked for and no
// column on this screen needs. So the export names its columns explicitly and takes only what an
// enrollment report is about: who they are, the work email that links them, and when they enrolled.
// If a future column is added to the wire it does NOT silently join this file.

import { extractList, sdk, type CompanyEmployeeOut } from '@givmo/api-client';
import {
  buildCsv,
  collectAllPages,
  downloadCsv,
  stampedCsvName,
  type SaveFile,
} from './csv-export';
import { employeeFullName } from '../employees/employees-decode';

/** The exported columns, in order. Deliberately narrow — see the module docstring. */
export const ENROLLMENT_COLUMNS = ['name', 'work_email', 'enrolled_at'] as const;
export type EnrollmentColumn = (typeof ENROLLMENT_COLUMNS)[number];

/**
 * An employee row as this export reads it. `linked_at` is the ENROLLMENT instant, additive on
 * `CompanyEmployeeOut` (EP-WEB-B6). It is typed optional and read defensively because the generated
 * client only learns the field after an api-client regen against the backend that added it — and
 * because a row flushed before the column existed can legitimately carry null.
 *
 * It is NOT interchangeable with `created_at`: that is the USER's ACCOUNT-creation date. Falling back to
 * it would silently report when someone joined Givmo rather than when they joined this employer's
 * program, which is precisely the wrong number for a file called "enrollment". So there is no fallback —
 * an unknown enrollment date exports EMPTY.
 */
type EnrollmentRow = CompanyEmployeeOut & { linked_at?: string | null };

/** Raised when a roster page's body is not a shape this export can trust. */
export class RosterPageShapeError extends Error {
  constructor() {
    super('The employee list returned an unexpected shape. The export was not saved.');
    this.name = 'RosterPageShapeError';
  }
}

/**
 * Whether a response body is a roster page this export may read: a bare array, or the
 * `{data: [...]}` envelope. Anything else — an object, a string, null, a `{data: {...}}` —
 * is NOT an empty page, it is an unreadable one.
 */
function isRosterPageBody(body: unknown): boolean {
  if (Array.isArray(body)) return true;
  if (typeof body === 'object' && body !== null && 'data' in body) {
    return Array.isArray((body as { data: unknown }).data);
  }
  return false;
}

/**
 * Fetch one page of the roster.
 *
 * **Strict, not tolerant — deliberately inverting the house decode rule.** `extractList`
 * is a TOLERANT reader: it turns any unexpected body into `[]` so a screen degrades to
 * "no rows" instead of crashing. That is right for a screen and WRONG here, because
 * `collectAllPages` reads a short page as PROOF OF EXHAUSTION — so a mangled or
 * shape-shifted 200 arriving mid-pagination would be silently read as "that was the end",
 * and the export would save a truncated file under a name that reads as complete. That is
 * precisely the failure this module exists to prevent.
 *
 * So the body is validated BEFORE `extractList` sees it, and an unreadable page THROWS.
 * For an export whose whole claim is completeness, failing loudly beats degrading quietly.
 */
async function fetchEmployeesPage(offset: number, limit: number): Promise<EnrollmentRow[]> {
  const { data } = await sdk.companiesGetCompanyEmployees({
    query: { 'page[limit]': limit, 'page[offset]': offset },
    throwOnError: true,
  });
  if (!isRosterPageBody(data)) throw new RosterPageShapeError();
  return extractList<EnrollmentRow>(data);
}

/**
 * Drop rows sharing an `id`, keeping first-seen order.
 *
 * **Why an export needs this at all.** The roster read is OFFSET-paginated over a
 * `created_at` ordering with no id tiebreaker, so the row set can shift under the export:
 * an employee enrolling or being removed mid-run moves the offset boundary. That yields two
 * failure modes, and they are not equally fixable here:
 *
 * * a row can be seen TWICE (a removal shifts later rows back across the boundary) — this
 *   dedupe removes it, and a duplicated employee in a finance file is the more damaging of
 *   the two since it inflates counts;
 * * a row can be SKIPPED (an insertion shifts rows forward) — inherent to offset pagination
 *   without a total-count oracle, and NOT fixable client-side. A stable backend cursor is
 *   the real fix; that is a backend change, not this export's.
 *
 * So the honest reading of this file is POINT-IN-TIME-ISH: it is the roster as it looked
 * across the run, not an atomic snapshot. Rosters change on the order of days and exports
 * take seconds, so the window is small — but it is not zero, and this comment exists so the
 * next reader does not mistake the dedupe for a completeness guarantee.
 */
function dedupeById(rows: readonly EnrollmentRow[]): EnrollmentRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const id = String(row.id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** The ISO date (YYYY-MM-DD) of an enrollment instant, or '' when unknown. Never guesses. */
function enrolledOn(row: EnrollmentRow): string {
  if (!row.linked_at) return '';
  const parsed = new Date(row.linked_at);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

/** Project the fetched rows onto {@link ENROLLMENT_COLUMNS}. Exported for its unit test. */
export function toEnrollmentRows(
  employees: readonly EnrollmentRow[],
): Record<EnrollmentColumn, string>[] {
  return employees.map((employee) => ({
    name: employeeFullName(employee),
    work_email: employee.work_email ?? '',
    enrolled_at: enrolledOn(employee),
  }));
}

/**
 * Build and download the enrollment CSV, returning the row count written.
 *
 * Throws on a failed page fetch, an unreadable page body, or a roster exceeding the page
 * ceiling — so the caller toasts and NO file is written. A partial roster saved under this
 * name would be read as the complete one.
 *
 * An EMPTY roster writes nothing at all: a header-only file is not a useful artifact, and
 * saving one while the toast says "no employees to export" would put two contradictory
 * statements in front of the admin. The zero is reported, not downloaded.
 *
 * `save` receives the finished file (`blobSave` by default; the Reports tiles may hold it for a
 * click, `csv-export.ts` § One save per interaction).
 */
export async function downloadEnrollmentCsv(save?: SaveFile): Promise<number> {
  const employees = dedupeById(await collectAllPages(fetchEmployeesPage));
  if (employees.length === 0) return 0;
  downloadCsv(
    buildCsv(ENROLLMENT_COLUMNS, toEnrollmentRows(employees)),
    stampedCsvName('enrollment'),
    save,
  );
  return employees.length;
}
